import { Box, IconButton, Typography } from '@mui/material';
import { useReducer, useImperativeHandle, forwardRef, useEffect, useMemo } from 'react';

import { TransparentButton } from 'app/shared-components/buttons';
import TrashIcon from 'app/assets/icons/trash.svg?react';
import PlusIcon from 'app/assets/icons/plus.svg?react';

import HealtheraInput from '../TextInput';
import RearrangeArrows from '../RearrangeArrows';
import RichTextEditor from '../RichTextEditor';

import reducer, { actionTypes, createEmptySection } from './contentEditorReducer';

const emptyContent = [createEmptySection()];

const EMPTY_HTML_CONTENT = '<p></p>';

function processContent(content) {
  return content
    .map((section) => {
      const isContentEmpty = !section?.content?.html || section.content.html === EMPTY_HTML_CONTENT;

      if (isContentEmpty && !section.title) {
        return null;
      }

      const processedSection = {};
      if (section.title) processedSection.title = section.title;
      if (!isContentEmpty) {
        processedSection.content = {
          html: section.content.html,
          raw: section.content.raw,
        };
      }

      return processedSection;
    })
    .filter(Boolean);
}

/**
 * ContentEditor component that exposes methods to parent component
 * @param {Object} props
 * @param {string} [props.label] - The label for the content editor
 * @param {string} [props.error] - The error message for the content editor
 * @param {Function} props.onImageUpload - The function to upload images
 * @param {Object} [props.value] - The initial value of the content editor
 * @param {React.Ref} ref - The ref to parent component
 * @returns {JSX.Element}
 */
const ContentEditor = forwardRef((props, ref) => {
  const { label = 'Content editor', onImageUpload, value, error } = props;
  const initialContent = useMemo(() => (value?.length ? value : emptyContent), [value]);

  const [contentState, dispatch] = useReducer(reducer, initialContent);

  useImperativeHandle(ref, () => ({
    resetContent: () => {
      dispatch({ type: actionTypes.RESET_CONTENT, initialContent });
    },
    getCurrentContent: () => processContent(contentState),
  }));

  useEffect(() => {
    dispatch({ type: actionTypes.RESET_CONTENT, initialContent });
  }, [initialContent]);

  return (
    <Box className="flex flex-col gap-20">
      {label && (
        <Typography component="label" className="capitalize font-500 text-typography-primary">
          {label}
        </Typography>
      )}
      {contentState.map((section, index) => (
        <Box key={index} className="flex gap-12 items-center">
          <RearrangeArrows
            moveUpDisabled={index === 0}
            onMoveUp={() =>
              dispatch({
                type: actionTypes.MOVE_SECTION,
                sectionIndex: index,
                direction: 'up',
              })
            }
            moveDownDisabled={index === contentState.length - 1}
            onMoveDown={() =>
              dispatch({
                type: actionTypes.MOVE_SECTION,
                sectionIndex: index,
                direction: 'down',
              })
            }
          />
          <Box className="flex flex-col gap-12 grow">
            <HealtheraInput
              value={section?.title || ''}
              onChange={(newTitle) =>
                dispatch({
                  type: actionTypes.UPDATE_SECTION_TITLE,
                  sectionIndex: index,
                  sectionTitle: newTitle,
                })
              }
              placeholder="Section title"
              error={error?.[index]?.title?.message}
            />
            <RichTextEditor
              value={section.content?.raw || ''}
              onChange={(newContentHTML, newContentJSON) =>
                dispatch({
                  type: actionTypes.UPDATE_SECTION_CONTENT,
                  sectionIndex: index,
                  sectionContent: {
                    html: newContentHTML,
                    raw: newContentJSON,
                  },
                })
              }
              placeholder="Section content"
              onImageUpload={onImageUpload}
              error={error?.[index]?.content?.html?.message}
            />
          </Box>
          <IconButton
            aria-label="Delete section"
            title="Delete section"
            className="p-0"
            disabled={contentState.length === 1}
            onClick={() =>
              dispatch({
                type: actionTypes.DELETE_SECTION,
                sectionIndex: index,
              })
            }
          >
            <TrashIcon className="h-16 w-16" />
          </IconButton>
        </Box>
      ))}
      <Box className="flex justify-end">
        <TransparentButton
          label="Add new section"
          icon={<PlusIcon className="h-16 w-16" />}
          cb={() => dispatch({ type: actionTypes.ADD_SECTION })}
          outlined
        />
      </Box>
    </Box>
  );
});

export default ContentEditor;
